import {
  Box,
  Card,
  Column,
  Columns,
  Heading,
  Hidden,
  Stack,
  StackSection,
  Text,
} from "@origin-digital/ods-core";
import { AppStoreBadge } from "@origin-digital/ods-lab";
import React from "react";
import { useAppContext } from "../../../../context/AppContext";
import { config } from "../../../../config";
import { content } from "./content";

type AppPromoUrls = NonNullable<IAppConfig["appPromo"]>;
type AppPromoContent = typeof content.appPromo & AppPromoUrls;

const MobileCopyBlock: React.FC<{ appPromo: AppPromoContent }> = ({
  appPromo,
}) => (
  <Hidden above="lg">
    <Stack space="medium" alignX="center">
      <Heading component="h2" variant="h2" align="center">
        {appPromo.heading}
      </Heading>
      <Text tone="neutralDark" align="center">
        {appPromo.description}
      </Text>
    </Stack>
  </Hidden>
);

const DesktopCopyBlock: React.FC<{ appPromo: AppPromoContent }> = ({
  appPromo,
}) => (
  <Hidden below="lg">
    <Stack space="large">
      <Stack space="xsmall">
        <Heading component="p" variant="h2">
          {appPromo.headingPrefix}
        </Heading>
        <Heading component="h2" variant="h1">
          {appPromo.headingHighlight}
        </Heading>
      </Stack>
      <Text variant="subtitle-small" tone="neutralDark">
        {appPromo.description}
      </Text>
      <Box marginTop={{ xs: "none", lg: "medium" }}>
        <Text variant="subtitle-small" tone="neutralDark">
          {appPromo.cta}
        </Text>
      </Box>
    </Stack>
  </Hidden>
);

const StoreBadgeLinks: React.FC<{ appPromo: AppPromoContent }> = ({
  appPromo,
}) => (
  <>
    <Hidden above="lg">
      <AppStoreBadge
        alignment="inline"
        appleDataId="app-store-link"
        androidDataId="google-play-link"
        appleHref={appPromo.appleStoreUrl}
        androidHref={appPromo.googlePlayUrl}
      />
    </Hidden>
    <Hidden below="lg">
      <AppStoreBadge
        alignment="stack"
        appleDataId="app-store-link"
        androidDataId="google-play-link"
        appleHref={appPromo.downloadPageUrl}
        androidHref={appPromo.downloadPageUrl}
      />
    </Hidden>
  </>
);

const QrCodeBlock: React.FC<{ appPromo: AppPromoContent }> = ({ appPromo }) => (
  <Hidden below="lg">
    <Box
      data-id="qr-code-placeholder"
      display="flex"
      padding="xxsmall"
      backgroundColor="white"
      borderRadius="small"
    >
      <img src={appPromo.qrCodeUrl} height="96" alt={appPromo.qrCodeAlt} />
    </Box>
  </Hidden>
);

const BadgeAndQrBlock: React.FC<{ appPromo: AppPromoContent }> = ({
  appPromo,
}) => (
  <Box
    display="flex"
    marginTop={{ xs: "large", lg: "large" }}
    alignItems="center"
    gap={{ xs: "none", lg: "xlarge" }}
    justifyContent={{ xs: "center", lg: "flex-start" }}
    width="full"
  >
    <StoreBadgeLinks appPromo={appPromo} />
    <QrCodeBlock appPromo={appPromo} />
  </Box>
);

const HeroImageBlock: React.FC<{ appPromo: AppPromoContent }> = ({
  appPromo,
}) => (
  <Box
    display="flex"
    overflow="hidden"
    justifyContent={{ xs: "center", lg: "flex-end" }}
  >
    <Hidden above="lg">
      <Box display="flex">
        <img
          data-id="hero-image-mobile"
          src={appPromo.heroImageMobileUrl}
          alt={appPromo.heroImageMobileAlt}
          height="210"
        />
      </Box>
    </Hidden>
    <Hidden below="lg">
      <Box display="flex">
        <img
          data-id="hero-image-desktop"
          width="100%"
          src={appPromo.heroImageDesktopUrl}
          alt={appPromo.heroImageDesktopAlt}
        />
      </Box>
    </Hidden>
  </Box>
);

const PromoLayout: React.FC<{ appPromo: AppPromoContent }> = ({ appPromo }) => (
  <StackSection width="xlarge" hideGutter={false}>
    <Card
      backgroundColor="secondary"
      paddingXSpecific="none"
      paddingYSpecific="none"
    >
      <Columns direction={{ xs: "column", lg: "row" }} space="none">
        <Column>
          <Box padding={{ xs: "xlarge", lg: "xxlarge", xl: "xxxlarge" }}>
            <MobileCopyBlock appPromo={appPromo} />
            <DesktopCopyBlock appPromo={appPromo} />
            <BadgeAndQrBlock appPromo={appPromo} />
          </Box>
        </Column>
        <Column alignSelf="flex-end" alignX={{ xs: "center", lg: "right" }}>
          <HeroImageBlock appPromo={appPromo} />
        </Column>
      </Columns>
    </Card>
  </StackSection>
);

export const OriginAppPromo: React.FC = () => {
  const { isNative } = useAppContext();
  const appPromoUrls = config.appPromo;

  if (isNative || !appPromoUrls) {
    return null;
  }

  const appPromo: AppPromoContent = {
    ...content.appPromo,
    ...appPromoUrls,
  };

  return <PromoLayout appPromo={appPromo} />;
};
